import React from "react";
import {
  CollectionReference,
  DocumentData,
  QueryConstraint,
  QueryDocumentSnapshot,
  WithFieldValue,
  and,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  updateDoc,
  where,
} from "firebase/firestore";

import { AuthContext, FirebaseContext } from "@contexts";
import {
  CollectionQueryComparator,
  Constraint,
  GetCollectionResp,
} from "./firestoreHookTypes";

function makeNotFetchedErrorMessage(
  collectionName: string,
  documentId: string,
) {
  return `Document ${collectionName}/${documentId} hasn't been fetched yet.`;
}

export type PaginationOpts = {
  size: number;
  page: number;
};

export const DEFAULT_PAGINATION_OPTS: PaginationOpts = {
  size: 0,
  page: 1,
};

export type CollectionConstraints =
  | {
      comparators?: CollectionQueryComparator[];
      constraints?: Constraint[];
    }
  | QueryConstraint[];

export const DEFAULT_COLLECTION_CONSTRAINTS: CollectionConstraints = [];

function buildQueryPaginationConstraints(
  paginationOpts: PaginationOpts,
  checkpoints: (QueryDocumentSnapshot | undefined)[],
) {
  if (paginationOpts.size === 0) return [];
  const limitConstraint = limit(paginationOpts.size);
  const pageConstraint =
    checkpoints[paginationOpts.page] === undefined
      ? undefined
      : startAfter(checkpoints[paginationOpts.page]);
  if (paginationOpts.page === 1 || pageConstraint === undefined)
    return [limitConstraint];
  return [pageConstraint, limitConstraint];
}

function buildQuery(
  collectionRef: CollectionReference,
  collectionConstraints: CollectionConstraints,
  paginationOpts: PaginationOpts,
  checkpoints: (QueryDocumentSnapshot | undefined)[],
) {
  const paginationConstraints = buildQueryPaginationConstraints(
    paginationOpts,
    checkpoints,
  );

  if (collectionConstraints instanceof Array) {
    return {
      data: query(
        collectionRef,
        ...collectionConstraints,
        ...paginationConstraints,
      ),
      count: query(collectionRef, ...collectionConstraints),
    };
  }

  const comparators = (collectionConstraints.comparators ?? []).map(
    (comparator) =>
      where(
        comparator.fieldOne,
        comparator.equalityComparator,
        comparator.fieldTwo,
      ),
  );

  const constraints = (collectionConstraints.constraints ?? [])
    .map(({ constraint, value }) => {
      if (constraint === "limit") return limit(value);
      if (constraint === "orderBy") return orderBy(value.key, value.direction);
    })
    .filter((constraint) => constraint !== undefined);

  return {
    data: query(
      collectionRef,
      and(...comparators),
      ...(constraints as Exclude<(typeof constraints)[number], undefined>[]),
      ...paginationConstraints,
    ),
    count: query(
      collectionRef,
      and(...comparators),
      ...(constraints as Exclude<(typeof constraints)[number], undefined>[]),
    ),
  };
}

/**
 * An abstraction for Firestore that queries the database collections and
 * provides an API for updating / deleting the comopnent documents.
 * This is meant to be wrapped by a `useGet<Collection>` hook.
 * @param {string} collectionName Collection being queried.
 * @param {CollectionQueryComparator[]} comparators comparators that define filter conditions for the collection query.
 * @returns {[T[], CollectionActions]} Array of documents in the collection + update / delete functions for those documents.
 */
export function useGetCollection<T>(
  collectionName: string,
  constraints: CollectionConstraints = DEFAULT_COLLECTION_CONSTRAINTS,
  paginationOpts: PaginationOpts = DEFAULT_PAGINATION_OPTS,
): GetCollectionResp<T> {
  const { authUser: user } = React.useContext(AuthContext);
  const { firestore } = React.useContext(FirebaseContext);
  // Documents fetched from Firestore
  const [documents, setDocuments] = React.useState<DocumentData[]>();
  // The number of documents in the total query.
  const [documentCount, setCount] = React.useState<number>();
  // A list of documents seen at various points.
  const [checkpoints, setCheckpoints] = React.useState<
    (QueryDocumentSnapshot | undefined)[]
  >([]);

  // Reset Checkpoints when the query changes.
  React.useEffect(() => {
    setCheckpoints([]);
  }, [constraints]);

  const collectionQueries = React.useMemo(
    () =>
      buildQuery(
        collection(firestore, collectionName),
        constraints,
        paginationOpts,
        checkpoints,
      ),
    [firestore, collectionName, constraints, paginationOpts, checkpoints],
  );

  // Submit the query. Store documents, checkpoints, and count.
  React.useEffect(() => {
    const { data: dataQuery, count: countQuery } = collectionQueries;
    return onSnapshot(dataQuery, (querySnapshot) => {
      getCountFromServer(countQuery).then((v) => {
        const { count } = v.data();
        setCount(count);
      });

      const snapshotDocuments: DocumentData[] = [];

      querySnapshot.forEach((document) =>
        snapshotDocuments.push({
          id: document.id,
          ...document.data(),
        }),
      );

      setDocuments(snapshotDocuments);

      // Only save checkpoint docs if you're paginating your query.
      if (paginationOpts.size !== 0) {
        setCheckpoints((checkpoints) => {
          checkpoints[paginationOpts.page + 1] =
            querySnapshot.docs[querySnapshot.docs.length - 1];
          return checkpoints;
        });
      }
    });
  }, [collectionQueries, user, setCount]);

  const updateCallback = React.useCallback(
    function doUpdate<UpdateT extends WithFieldValue<DocumentData>>(
      document: DocumentData,
      data: UpdateT,
    ) {
      const documentReference = doc(firestore, collectionName, document.id);
      if (!documentReference) {
        throw new Error(
          makeNotFetchedErrorMessage(collectionName, document.id),
        );
      }

      return updateDoc(documentReference, data);
    },
    [firestore, collectionName],
  );

  const deleteCallback = React.useCallback(
    function doDelete(document: DocumentData) {
      const documentReference = doc(firestore, collectionName, document.id);
      if (!documentReference) {
        throw new Error(
          makeNotFetchedErrorMessage(collectionName, document.id),
        );
      }

      return deleteDoc(documentReference);
    },
    [firestore, collectionName],
  );

  return {
    documents: documents as T[] | undefined,
    count: documentCount,
    helpers: { update: updateCallback, delete: deleteCallback },
  };
}
